import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { stripInlineMarkdown } from './InlineMarkdown';

/** Must match exactly (not translated). */
export const DELETE_SAFE_WORD = 'EDeleteVise';

/**
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {'press' | 'hiring'} props.variant
 * @param {'archive' | 'remove'} [props.mode]
 * @param {string} props.slug
 * @param {string} props.itemTitle
 * @param {() => void | Promise<void>} props.onSuccess
 */
const DeletePostConfirmModal = ({ isOpen, onClose, variant, slug, itemTitle, onSuccess, mode = 'archive' }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const [error, setError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const isRemove = mode === 'remove';

  useEffect(() => {
    if (isOpen) {
      setValue('');
      setError(null);
      setDeleting(false);
    }
  }, [isOpen, slug, mode]);

  if (!isOpen) return null;

  const apiUrl = variant === 'press' ? `/api/press-releases/${encodeURIComponent(slug)}` : `/api/hiring/${encodeURIComponent(slug)}`;
  const titleKey = isRemove
    ? (variant === 'press' ? 'deleteConfirm.removeTitlePress' : 'deleteConfirm.removeTitleHiring')
    : (variant === 'press' ? 'deleteConfirm.titlePress' : 'deleteConfirm.titleHiring');
  const canSubmit = value === DELETE_SAFE_WORD && !deleting;

  const handleConfirm = async () => {
    if (!canSubmit) return;
    setError(null);
    setDeleting(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(isRemove ? `${apiUrl}/delete` : apiUrl, {
        method: isRemove ? 'POST' : 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        throw new Error(data?.message || t(isRemove ? 'deleteConfirm.removeError' : 'deleteConfirm.error'));
      }
      if (onSuccess) await onSuccess();
      onClose();
    } catch (e) {
      setError(e.message || t(isRemove ? 'deleteConfirm.removeError' : 'deleteConfirm.error'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[250] bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="delete-confirm-title"
    >
      <div
        className="w-full max-w-md bg-surface rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b">
          <h3 id="delete-confirm-title" className="text-lg font-semibold text-ink">
            {t(titleKey, { title: stripInlineMarkdown(itemTitle || '') })}
          </h3>
          <p className="mt-2 text-sm text-ink-muted">{t(isRemove ? 'deleteConfirm.removeIntro' : 'deleteConfirm.intro')}</p>
          <p className="mt-3 text-sm text-ink-muted">{t('deleteConfirm.instruction')}</p>
          <p
            className="mt-2 font-mono text-sm font-semibold text-ink select-all"
            aria-label={DELETE_SAFE_WORD}
          >
            {DELETE_SAFE_WORD}
          </p>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label htmlFor="delete-safe-word-input" className="block text-sm font-medium text-ink-muted mb-1">
              {t('deleteConfirm.inputLabel')}
            </label>
            <input
              id="delete-safe-word-input"
              type="text"
              autoComplete="off"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setError(null);
              }}
              className="w-full border border-edge-strong rounded-lg p-2 text-sm font-mono"
              placeholder=""
            />
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
          {value.length > 0 && value !== DELETE_SAFE_WORD && (
            <p className="text-sm text-amber-700">{t('deleteConfirm.mismatch')}</p>
          )}
        </div>
        <div className="p-4 border-t flex justify-end gap-2 bg-canvas">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="px-4 py-2 rounded-lg border border-edge-strong text-ink-muted hover:bg-surface text-sm disabled:opacity-50"
          >
            {t('deleteConfirm.cancel')}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canSubmit}
            className={`px-4 py-2 rounded-lg text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed ${
              isRemove ? 'bg-danger hover:bg-danger/80' : 'bg-amber-600 hover:bg-amber-700'
            }`}
          >
            {deleting
              ? t(isRemove ? 'deleteConfirm.removing' : 'deleteConfirm.deleting')
              : t(isRemove ? 'deleteConfirm.confirmRemove' : 'deleteConfirm.confirmDelete')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeletePostConfirmModal;
